import type { ToolComponentProps } from '@kanbu/chatbot-ui';
import { Trans } from '@lingui/react/macro';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

function stringify(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function Section({ label, value }: { label: React.ReactNode; value: string }) {
  if (!value) {
    return null;
  }

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-muted-foreground text-xs font-medium'>{label}</span>
      <pre className='max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-background/50 p-2 text-xs'>
        {value}
      </pre>
    </div>
  );
}

/**
 * Compact, collapsed-by-default inspector rendered under every tool call in
 * the admin inbox — lets support staff see the exact call arguments and the
 * returned result/artifact without leaving the conversation.
 */
export function AdminToolCallDetails({
  toolCall,
  content,
  artifact,
  error,
}: ToolComponentProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className='w-full text-left'>
      <button
        type='button'
        onClick={() => setOpen(previous => !previous)}
        className='text-muted-foreground inline-flex items-center gap-1 text-xs transition-colors hover:text-foreground'
      >
        <ChevronRight
          className={`size-3 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className='font-mono'>{toolCall.name}</span>
        <Trans>call details</Trans>
      </button>

      {open && (
        <div className='mt-1 flex flex-col gap-2 rounded border border-border p-2'>
          <Section
            label={<Trans>Arguments</Trans>}
            value={stringify(toolCall.args)}
          />
          <Section
            label={<Trans>Result</Trans>}
            value={stringify(content ?? toolCall.content)}
          />
          <Section
            label={<Trans>Artifact</Trans>}
            value={stringify(artifact ?? toolCall.artifact)}
          />
          {error && (
            <Section label={<Trans>Error</Trans>} value={stringify(error)} />
          )}
        </div>
      )}
    </div>
  );
}
